import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HistoryIcon from '@mui/icons-material/History'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import dayjs from 'dayjs'

import { SessionStatusBarProps } from '../../../interfaceTypes/props'

/**
 * Tells the user, at a glance, which reconciliation draft their ticks are going into — the screen otherwise
 * looks identical whether a session is active or not, so it was impossible to know what you were working in
 * (or that nothing was being saved at all, since sessions are only created explicitly).
 */
const SessionStatusBar = ({
  hasSession,
  accountName,
  accountSortCode,
  statementMonth,
  selectedCount,
  saveStatus,
  pausedSession,
  onResume
}: SessionStatusBarProps) => {
  const saveLabel =
    saveStatus === 'saving'
      ? 'Saving…'
      : saveStatus === 'unsaved'
      ? 'Unsaved changes'
      : saveStatus === 'error'
      ? 'Last save failed'
      : saveStatus === 'saved'
      ? 'All changes saved'
      : ''

  // Stepped out of a session that is still open for this bank. Distinct from "no session at all": nothing is
  // being saved right now, but there IS a draft waiting, and saying so stops it reading as lost work.
  if (!hasSession && pausedSession) {
    return (
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mx={2}
        px={1.5}
        py={0.75}
        borderRadius={1}
        flexWrap="wrap"
        sx={theme => ({
          border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
          bgcolor: alpha(theme.palette.info.main, 0.08)
        })}
      >
        <PauseCircleOutlineIcon fontSize="small" color="info" />
        <Typography fontSize="13px">
          You've left the session for this bank (<b>{dayjs(pausedSession.statementMonth).format('MMMM YYYY')}</b>,{' '}
          <b>{pausedSession.itemCount}</b> transaction{pausedSession.itemCount === 1 ? '' : 's'}). It's saved —
          ticks you make now are not part of it.
        </Typography>
        {onResume ? (
          <Button size="small" variant="outlined" onClick={onResume} sx={{ ml: 'auto', py: 0 }}>
            Resume session
          </Button>
        ) : null}
      </Box>
    )
  }

  if (!hasSession) {
    return (
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mx={2}
        px={1.5}
        py={0.75}
        borderRadius={1}
        sx={theme => ({
          border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
          bgcolor: alpha(theme.palette.warning.main, 0.08)
        })}
      >
        <CloudOffIcon fontSize="small" sx={theme => ({ color: theme.palette.warning.dark })} />
        <Typography fontSize="13px">
          <b>No active session.</b> Anything you tick here is not being saved. Click on <b>Sessions</b>  dropdown and choose{' '}
          <b>Start session for this bank</b> to keep your progress.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      mx={2}
      px={1.5}
      py={0.75}
      borderRadius={1}
      flexWrap="wrap"
      sx={theme => ({
        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
        bgcolor: alpha(theme.palette.primary.main, 0.06)
      })}
    >
      <HistoryIcon fontSize="small" color="primary" />
      <Typography fontSize="13px">
        Working in session:{' '}
        <b>
          {accountName || 'This bank'}
          {accountSortCode ? ` (#${accountSortCode})` : ''}
        </b>
        {statementMonth ? (
          <>
            {' · statement month '}
            <b>{dayjs(statementMonth).format('MMMM YYYY')}</b>
          </>
        ) : null}
        {' · '}
        <b>{selectedCount}</b> transaction{selectedCount === 1 ? '' : 's'} selected
      </Typography>

      {saveLabel ? (
        <Box display="flex" alignItems="center" gap={0.5} ml="auto">
          {saveStatus === 'saving' ? (
            <CircularProgress size={13} />
          ) : saveStatus === 'error' ? (
            <ErrorOutlineIcon fontSize="small" color="error" />
          ) : saveStatus === 'saved' ? (
            <CheckCircleOutlineIcon fontSize="small" color="success" />
          ) : null}
          <Typography
            fontSize="12px"
            color={saveStatus === 'error' ? 'error.main' : 'text.secondary'}
            fontWeight={saveStatus === 'unsaved' || saveStatus === 'error' ? 600 : 400}
          >
            {saveLabel}
          </Typography>
        </Box>
      ) : null}
    </Box>
  )
}

export default SessionStatusBar
